import { useCallback, useState } from 'react';

/**
 * Tobias 2026-09-25: „Egal wo in der Werkbank ich einen Filter setze, er soll
 * gesetzt bleiben, damit ich genau da weiterarbeiten kann." Filter, Suche und
 * Seitenzahl der Listen leben deshalb im sessionStorage des Browser-Tabs:
 * Sprung in einen Beleg und zurück, Wechsel über die Sidebar oder ein Reload
 * behalten sie; ein neuer Tag in einem neuen Tab beginnt ohne alte Filter.
 * Gesperrtes Storage (privater Modus) → die Vorgabe gilt, nichts bricht.
 */
const PREFIX = 'wb.filter.';

export function leseGemerkt<T>(schluessel: string, vorgabe: T): T {
  try {
    const roh = window.sessionStorage.getItem(PREFIX + schluessel);
    if (roh === null) return vorgabe;
    const wert = JSON.parse(roh) as unknown;
    // Nur typgleiche Werte übernehmen (null-Vorgabe erlaubt String/null).
    if (vorgabe === null) return (wert === null || typeof wert === 'string' ? wert : vorgabe) as T;
    return typeof wert === typeof vorgabe ? (wert as T) : vorgabe;
  } catch {
    return vorgabe;
  }
}

export function schreibeGemerkt(schluessel: string, wert: unknown): void {
  try {
    window.sessionStorage.setItem(PREFIX + schluessel, JSON.stringify(wert));
  } catch {
    /* Gedächtnis ist optional. */
  }
}

/** `useState` mit Gedächtnis je Schlüssel (sessionStorage des Tabs). */
export function useGemerkt<T>(
  schluessel: string,
  vorgabe: T,
): [T, (wert: T | ((vorher: T) => T)) => void] {
  const [wert, setWert] = useState<T>(() => leseGemerkt(schluessel, vorgabe));
  const setze = useCallback(
    (neu: T | ((vorher: T) => T)) =>
      setWert((vorher) => {
        const naechster = typeof neu === 'function' ? (neu as (v: T) => T)(vorher) : neu;
        schreibeGemerkt(schluessel, naechster);
        return naechster;
      }),
    [schluessel],
  );
  return [wert, setze];
}
